import React, { Component, ReactNode } from 'react';
import { logger } from '../lib/logger';
import { auth } from '../lib/firebase';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorDetails?: {
    message: string;
    stack?: string;
    componentStack?: string;
  };
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
    logger.info('ErrorBoundary initialized', { component: 'ErrorBoundary' });
  }

  static getDerivedStateFromError(error: Error): State {
    logger.error('ErrorBoundary getDerivedStateFromError called', error, {
      component: 'ErrorBoundary',
      timestamp: new Date().toISOString()
    });
    return {
      hasError: true,
      errorDetails: {
        message: error.message,
        stack: error.stack
      }
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const currentUser = auth.currentUser;

    logger.group('ErrorBoundary caught an error');
    logger.error('Error details', error, {
      component: 'ErrorBoundary',
      userId: currentUser?.uid,
      userEmail: currentUser?.email,
      authInitialized: !!currentUser,
      errorMessage: error.message,
      errorName: error.name,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href
    });
    logger.groupEnd();

    this.setState({
      errorDetails: {
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack
      }
    });
  }

  handleReload = (): void => {
    logger.info('User clicked reload button', {
      component: 'ErrorBoundary',
      userId: auth.currentUser?.uid
    });
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      logger.warn('ErrorBoundary rendering error UI', {
        component: 'ErrorBoundary',
        errorMessage: this.state.errorDetails?.message
      });
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#4D4D4D] to-[#FFFFFF]">
          <div className="text-center space-y-6 max-w-md mx-auto px-4">
            <h1 className="text-2xl font-semibold text-gray-800">
              Something went wrong
            </h1>
            <p className="text-gray-600">
              The app encountered an unexpected error. Please reload to try again.
            </p>
            <button
              onClick={this.handleReload}
              className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-2xl transition-all duration-300"
            >
              Reload App
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
